import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  GroupIcon,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import AssessmentIcon from '@material-ui/icons/Assessment';
import BrokenImageIcon from '@material-ui/icons/BrokenImage';
import DeveloperModeIcon from '@material-ui/icons/DeveloperMode';
import EditIcon from '@material-ui/icons/Edit';
import ExtensionIcon from '@material-ui/icons/Extension';
import FormatShapesIcon from '@material-ui/icons/FormatShapes';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import MapIcon from '@material-ui/icons/MyLocation';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import ShareIcon from '@material-ui/icons/Share';
import WidgetsRoundedIcon from '@material-ui/icons/WidgetsRounded';
import { SidebarLogo } from './SidebarLogo';
import { SidebarVersion } from './SidebarVersion';

const organisationGraphQuery = new URLSearchParams([
  ['rootEntityRefs[]', 'group:default/re-engineering'],
  ['rootEntityRefs[]', 'group:default/re-product'],
  ['rootEntityRefs[]', 'group:default/re-salesforce'],
  ['rootEntityRefs[]', 'group:default/re-b2c-marketing'],
  ['selectedKinds[]', 'group'],
  ['selectedKinds[]', 'user'],
  ['selectedRelations[]', 'parentOf'],
  ['selectedRelations[]', 'hasMember'],
  ['maxDepth', '∞'],
  ['unidirectional', 'false'],
  ['direction', 'TB'],
]).toString();

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const catalogNavItem = navItems.take('page:catalog');
      const catalogGraphNavItem = navItems.take('page:catalog-graph');
      const searchNavItem = navItems.take('page:search');
      const apiDocsNavItem = navItems.take('page:api-docs');
      const techDocsNavItem = navItems.take('page:techdocs');
      const scaffolderNavItem = navItems.take('page:scaffolder');
      const unprocessedNavItem = navItems.take(
        'page:catalog-unprocessed-entities',
      );
      const techRadarNavItem = navItems.take('page:tech-radar');
      const surveyInsightsNavItem = navItems.take('page:survey-insights');
      const visualizerNavItem = navItems.take('page:app-visualizer');
      const userSettingsNavItem = navItems.take('page:user-settings');
      const catalogHref = catalogNavItem?.href ?? '/catalog';
      const catalogGraphHref = catalogGraphNavItem?.href ?? '/catalog-graph';
      const organisationGraphHref = `${catalogGraphHref}?${organisationGraphQuery}`;
      const searchHref = searchNavItem?.href ?? '/search';
      const apiDocsHref = apiDocsNavItem?.href ?? '/api-docs';
      const techDocsHref = techDocsNavItem?.href ?? '/docs';
      const scaffolderHref = scaffolderNavItem?.href ?? '/create';
      const unprocessedHref =
        unprocessedNavItem?.href ?? '/catalog-unprocessed-entities';
      const techRadarHref = techRadarNavItem?.href ?? '/tech-radar';
      const surveyInsightsHref =
        surveyInsightsNavItem?.href ?? '/survey-insights';
      const userSettingsHref = userSettingsNavItem?.href ?? '/settings';

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarVersion />
          <SidebarGroup label="Search" icon={<SearchIcon />} to={searchHref}>
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem
              icon={catalogNavItem ? () => catalogNavItem.icon : HomeIcon}
              to={catalogHref}
              text="Home"
              end
            >
              <SidebarSubmenu title="Home">
                <SidebarSubmenuItem
                  title="All catalog"
                  to={catalogHref}
                  exact
                />
                <SidebarSubmenuItem
                  icon={WidgetsRoundedIcon}
                  title="Libraries"
                  to={`${catalogHref}?filters%5Bkind%5D=component&filters%5Btype%5D=library&filters%5Buser%5D=all`}
                />
                <SidebarSubmenuItem
                  icon={GroupIcon}
                  title="Groups"
                  to={organisationGraphHref}
                />
                <SidebarSubmenuItem
                  icon={ShareIcon}
                  title="Graph"
                  to={organisationGraphHref}
                />
              </SidebarSubmenu>
            </SidebarItem>
            <SidebarItem
              icon={apiDocsNavItem ? () => apiDocsNavItem.icon : ExtensionIcon}
              to={apiDocsHref}
              text="APIs"
            />
            <SidebarItem
              icon={
                techDocsNavItem ? () => techDocsNavItem.icon : LibraryBooksIcon
              }
              to={techDocsHref}
              text="Docs"
            >
              <SidebarSubmenu title="Docs">
                <SidebarSubmenuItem title="All docs" to={techDocsHref} exact />
                <SidebarSubmenuItem
                  icon={FormatShapesIcon}
                  title="ADRs"
                  to="/catalog/default/component/smg-re-adr/docs"
                />
                <SidebarSubmenuItem
                  icon={DeveloperModeIcon}
                  title="Architecture"
                  to="/catalog/default/component/architecture/docs/"
                />
                <SidebarSubmenuItem
                  icon={EditIcon}
                  title="Contribute"
                  to="/catalog/default/component/backstage-documentation/docs"
                />
              </SidebarSubmenu>
            </SidebarItem>
            <SidebarItem
              icon={
                scaffolderNavItem
                  ? () => scaffolderNavItem.icon
                  : CreateComponentIcon
              }
              to={scaffolderHref}
              text="Create..."
            />
            <SidebarDivider />
            <SidebarScrollWrapper>
              <SidebarItem
                icon={
                  unprocessedNavItem
                    ? () => unprocessedNavItem.icon
                    : BrokenImageIcon
                }
                to={unprocessedHref}
                text="Unprocessed"
              />
            </SidebarScrollWrapper>
            <SidebarDivider />
            <SidebarScrollWrapper>
              <SidebarItem
                icon={techRadarNavItem ? () => techRadarNavItem.icon : MapIcon}
                to={techRadarHref}
                text="Tech Radar"
              />
              <SidebarItem
                icon={
                  surveyInsightsNavItem
                    ? () => surveyInsightsNavItem.icon
                    : AssessmentIcon
                }
                to={surveyInsightsHref}
                text="Survey Insights"
              />
              {visualizerNavItem && (
                <SidebarItem
                  icon={() => visualizerNavItem.icon}
                  to={visualizerNavItem.href}
                  text="Visualizer"
                />
              )}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to={userSettingsHref}
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
